import React from 'react';
import { Box, InputLabel, RadioGroup, FormControlLabel, Radio, FormHelperText, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from 'components/elements';
import { ThemeMode } from 'types/config';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
interface RadioGroupFieldProps {
  name: string;
  label: string;
  options: { label: string; value: string; disabled?: boolean; tooltip?: string }[];
  error?: string | false;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, selectedValue?: string) => void; // Optional custom onChange handler
  onChangeOverride?: boolean; // New property to toggle behavior
  disabled?: boolean;
  required?: boolean;
  className?: string;
  infoToolTip?: string;
  info?: boolean;
  isFormattedToolTip?: boolean;
}

const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  name,
  label,
  options,
  error,
  onChange,
  onChangeOverride = false, // Default behavior: use Formik's default logic
  disabled,
  required,
  info,
  infoToolTip,
  isFormattedToolTip = false,
  className = ''
}) => {
  const [field, meta, helpers] = useField(name);
  const theme = useTheme();

  return (
    <Box>
      <InputLabel className="form-input-label">
        <span>
          {label}
          {required ? ' *' : ''}
        </span>
        {info && (
          <Tooltip
            title={
              isFormattedToolTip ? (
                <Box>
                  {infoToolTip
                    ?.split('.')
                    .filter(Boolean)
                    .map((line, index) => (
                      <Typography key={index} sx={{ whiteSpace: 'pre-wrap', mb: 1, fontSize: '0.70rem' }}>
                        {line.trim() + '.'}
                      </Typography>
                    ))}
                </Box>
              ) : (
                infoToolTip || 'ToolTip'
              )
            }
          >
            <InfoOutlinedIcon fontSize="small" className="info-icon" />
          </Tooltip>
        )}
      </InputLabel>
      <RadioGroup
        row
        {...field}
        onChange={(e) => {
          const selectedValue = e.target.value;

          if (onChangeOverride && onChange) {
            e.preventDefault();
            onChange(e, selectedValue);
          } else {
            helpers.setValue(selectedValue);
          }
        }}
        className={className}
        sx={{
          background:
            theme.palette.mode === ThemeMode.LIGHT ? theme.palette.secondary.colorNeutrals?.neutrals20 : theme.palette.background.default
        }}
      >
        {options.map((option) => {
          const isDisabledTrue = option.value === 'true' && disabled === true;
          let labelColorStyle = undefined;
          if (isDisabledTrue) {
            labelColorStyle = { '.MuiFormControlLabel-label.Mui-disabled': { color: 'Black' } };
          }
          const radio = (
            <FormControlLabel
              key={option.value}
              control={<Radio checkedIcon={<RadioButtonCheckedIcon sx={{ fontSize: 20 }} />} />}
              label={option.label}
              value={option.value}
              checked={String(field.value) === String(option.value)}
              disabled={option.disabled ? option.disabled : disabled}
              sx={labelColorStyle}
            />
          );

          // Show tooltip only if the option is disabled and tooltip text exists
          if (option.disabled && option.tooltip) {
            return (
              <Tooltip key={option.value} title={option.tooltip} arrow>
                {radio}
              </Tooltip>
            );
          }

          return radio;
        })}
      </RadioGroup>
      {meta.touched && error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default RadioGroupField;
